import { useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ChevronDown, CircleCheck, MessageSquareOff, Paperclip, Send } from 'lucide-react';
import { ActionStack, Screen } from '@/app/layout/Screen';
import { Button } from '@/components/ui/Button';
import { ChipButton, Composer } from '@/components/ui/Chat';
import { PanelSkeleton, StateView } from '@/components/ui/Feedback';
import { Badge, Helper, Panel } from '@/components/ui/Surface';
import { TaskText } from '@/components/ui/TaskText';
import { useAiChatStore } from '@/store/aiChatStore';
import { useUiStore } from '@/store/uiStore';
import { useHaptic } from '@/app/hooks/useHaptic';
import { getBridge } from '@/platform';
import { AttachmentButton, ChatMessages } from './chat/ChatMessages';
import { FilePreview } from '@/components/ui/FilePreview';
import { ChatFaultView, faults } from './chat/faults';
import { RecordingBar } from './chat/VoiceParts';
import { useVoiceRecorder } from './chat/useVoiceRecorder';
import { useAssignmentTask } from './chat/useAssignmentTask';
import { useAttachmentPreview } from './chat/useAttachmentPreview';
import { useChatLiveUpdates, useChatSession } from './chat/useChatSession';

/**
 * Экран 21 в режиме реального бэкенда. Диалог с ИИ по заданию.
 *
 * Состояния: открытие чата, ошибка с восстановлением, продолженный диалог,
 * переписка, отправленный ответ.
 *
 * Чат живёт на сервере: активный диалог один и переживает закрытие приложения,
 * поэтому экран при открытии всегда вызывает `open` — сервер сам решает,
 * создать чат или вернуть текущий.
 *
 * Части диалога живут рядом в `chat/`: лента сообщений, просмотр вложения,
 * экраны сбоев и запись голоса. Здесь остаётся только их связывание.
 */

export function AssignmentChatScreen() {
  const { assignmentId = '' } = useParams();
  const [searchParams] = useSearchParams();
  // Вариант выбирается на экране урока и должен дойти до ассистента: без него
  // он получит задание без условия
  const variantId = searchParams.get('variant') ?? undefined;
  // Диалог инструмента остаётся в своей вкладке
  const fromTools = searchParams.get('from') === 'ai';
  const navigate = useNavigate();
  const haptic = useHaptic();
  const toast = useUiStore((state) => state.toast);
  const voice = useVoiceRecorder();

  const {
    messages,
    awaitingReply,
    resumed,
    starting,
    loadingHistory,
    sending,
    uploading,
    finished,
    opened,
    runPending,
    operations,
    error,
    fault,
    open,
    restart,
    send,
    attach,
    sendVoice,
    preview: requestPreview,
    previewReady,
    submit,
    cancel,
  } = useAiChatStore();

  const [input, setInput] = useState('');
  /**
   * Задание свёрнуто по умолчанию.
   *
   * Ассистент ведёт по вопросам сам, по одному; полный текст сверху сбивал с
   * этого порядка — человек начинал отвечать сразу на всё. Развернуть его
   * по-прежнему можно в одно касание.
   */
  const [taskOpen, setTaskOpen] = useState(false);

  const fileRef = useRef<HTMLInputElement>(null);
  const lesson = useAssignmentTask(assignmentId, variantId);
  const attachment = useAttachmentPreview();

  useChatSession(assignmentId, variantId);
  useChatLiveUpdates(finished);

  const subtitle = fromTools ? 'Инструмент ИИ' : 'Домашнее задание';
  const tab = fromTools ? 'ai' : undefined;

  if (starting) {
    return (
      <Screen subtitle={subtitle} back tab={tab}>
        <PanelSkeleton count={3} />
      </Screen>
    );
  }

  if (finished) {
    return (
      <Screen subtitle={subtitle} back backTo={fromTools ? '/assistant' : '/course'} tab={tab}>
        <StateView
          icon={<CircleCheck className="h-6 w-6" aria-hidden />}
          title="Ответ отправлен"
          description="Ассистент обрабатывает ответ. Отметка о сдаче появится в списке уроков, когда обработка закончится."
          primary={
            <Button variant="primary" block onClick={() => navigate('/course')}>
              Вернуться к программе
            </Button>
          }
        />
      </Screen>
    );
  }

  // Экран ошибки заменяет диалог только когда его не удалось открыть. Если чат
  // открыт, сбой отправки показывается подсказкой: подменять экран нельзя —
  // вместе с ним исчезает набранный, но не отправленный ответ
  if (fault && !opened) {
    return (
      <Screen subtitle={subtitle} back tab={tab}>
        <ChatFaultView
          fault={fault}
          onRetry={() =>
            void (fault === 'busy' ? restart(assignmentId, variantId) : open(assignmentId, variantId))
          }
        />
      </Screen>
    );
  }

  const onPickFile = async (file: File | undefined) => {
    if (!file) return;
    if (await attach(file)) haptic.notification('success');
  };

  const showPreview = async () => {
    // Итог — задача в очереди: ассистент присылает его в диалог отдельным
    // сообщением, поэтому здесь только ожидание, без своего окна
    const outcome = await requestPreview();

    if (outcome === 'ready') {
      toast('Итог готов — можно отправлять на проверку', 'success');
    } else if (outcome === 'answered') {
      // Агент не собрал итог, а задал вопросы: ожидание файла снято, и
      // человеку нужно ответить, а не ждать дальше
      toast('Ассистенту не хватает ответов — ответьте на его вопросы и соберите итог снова', 'info');
    } else if (outcome === 'timeout') {
      toast('Ассистент ещё собирает итог', 'info');
    }
  };

  const onSubmit = async () => {
    const confirmed = await getBridge().showConfirm(
      'Отправить ответ на проверку? После отправки диалог завершится.'
    );
    if (!confirmed) return;

    if (await submit()) {
      haptic.notification('success');
      return;
    }
    // Причина отказа объясняется отдельным окном: подсказки под полем ввода
    // на телефоне не видно, и нажатие выглядит как бездействие
    await getBridge().showAlert(
      useAiChatStore.getState().error ?? 'Ответ пока не принят. Попробуйте ещё раз через минуту.'
    );
  };

  const onCancel = async () => {
    // Завершение удаляет переписку на сервере: без предупреждения человек
    // теряет всё, что надиктовал и написал по заданию
    const confirmed = await getBridge().showConfirm(
      'Завершить диалог? Вся история общения с ассистентом будет удалена, ответ не отправится на проверку.'
    );
    if (!confirmed) return;
    if (await cancel()) navigate('/course');
    else toast('Не удалось завершить диалог', 'error');
  };

  // Пока ассистент готовит материал, диалог остаётся живым: можно писать
  // дальше. Заблокированным остаётся только то, что нельзя запускать повторно
  const sendMessage = async () => {
    const text = input;
    // Поле очищается сразу, чтобы диалог ощущался живым, но при неудаче текст
    // возвращается: заставлять человека набирать ответ заново недопустимо
    setInput('');
    if (!(await send(text))) setInput((current) => current || text);
  };

  const sendRecording = async () => {
    const blob = await voice.stop();
    if (!blob) return;
    if (await sendVoice(blob, voice.lastDurationMs)) haptic.notification('success');
  };

  // Заблокировано то, что нельзя запускать повторно, пока идёт предыдущее
  const locked = sending || uploading || loadingHistory || runPending;

  /**
   * Есть ли что сдавать.
   *
   * Итог собирается из переписки, поэтому пустой диалог сдавать нечем. Но
   * ответ пользователя нужен не всегда: часть заданий агент выполняет сам и
   * присылает готовый материал — требовать реплику в таком диалоге незачем.
   */
  const hasDialogue = messages.length > 0;

  /**
   * Сдавать можно только собранный итог.
   *
   * Так устроен сервер: ответ принимается после того, как ассистент прислал
   * готовый материал. И так честнее по сути — человек отправляет на проверку
   * то, что сам увидел, а не то, что соберётся неизвестно как.
   */
  const canSubmit = previewReady();

  /**
   * Следующий шаг по заданию: по нему выбирается главная кнопка.
   *
   * Сборка итога и отправка — оба долгие действия с флагом `runPending`, и их
   * нужно различать: во время сборки главная кнопка говорит о сборке, во время
   * отправки — об отправке.
   */
  const step: 'answer' | 'preview' | 'collecting' | 'submit' = !hasDialogue
    ? 'answer'
    : canSubmit
      ? 'submit'
      : runPending
        ? 'collecting'
        : 'preview';
  const submitting = runPending && canSubmit;

  // Готовые итоги задач: итог приходит операцией с PDF, а не текстом
  const readyOperations = operations.filter(
    (operation) => operation.status === 'ready' && (operation.attachments?.length ?? 0) > 0
  );

  const composer = (
    <>
      {voice.recording ? <RecordingBar level={voice.level} elapsedMs={voice.elapsedMs} /> : null}
      {voice.error ? <Helper error>{voice.error}</Helper> : null}

      <Composer
        value={input}
        onChange={setInput}
        sending={sending}
        placeholder="Ваш ответ ассистенту"
        onSubmit={() => void sendMessage()}
        recording={voice.recording}
        onVoiceStart={() => void voice.start()}
        onVoiceStop={() => void sendRecording()}
        onVoiceCancel={voice.cancel}
        actions={
          <>
            <ChipButton
              icon={<Paperclip className="h-3.5 w-3.5" aria-hidden />}
              disabled={locked || voice.recording}
              onClick={() => fileRef.current?.click()}
            >
              {uploading ? 'Отправляем файл…' : 'Прикрепить файл'}
            </ChipButton>
            {/*
              Сборка итога — главная кнопка экрана. Здесь остаётся только
              пересборка: после готового итога человек может дописать ответы,
              и прежний материал устареет
            */}
            {step === 'submit' ? (
              <ChipButton
                icon={<Send className="h-3.5 w-3.5" aria-hidden />}
                disabled={locked || voice.recording}
                onClick={() => void showPreview()}
              >
                {runPending ? 'Ассистент работает…' : 'Пересобрать итог'}
              </ChipButton>
            ) : null}
          </>
        }
      />
    </>
  );

  return (
    <Screen subtitle={subtitle} back tab={tab} footer={composer}>
      {resumed ? (
        <Panel tone="tint" className="mb-3">
          <Badge tone="muted">Продолжение</Badge>
          <p className="mt-2 text-[12.5px] leading-[1.45]">
            Возвращаемся к начатому диалогу. Всё, что вы написали раньше, сохранено.
          </p>
        </Panel>
      ) : null}

      {lesson ? (
        <Panel className="mb-3">
          <button
            type="button"
            aria-expanded={taskOpen}
            onClick={() => setTaskOpen(!taskOpen)}
            className="bd-focus flex w-full items-center justify-between gap-2 text-left"
          >
            <strong className="text-[13px] font-medium">{lesson.title}</strong>
            <ChevronDown
              className={taskOpen ? 'h-4 w-4 shrink-0 rotate-180 text-muted' : 'h-4 w-4 shrink-0 text-muted'}
              aria-hidden
            />
          </button>
          {taskOpen ? <TaskText text={lesson.description} className="mt-2" /> : null}
        </Panel>
      ) : null}

      {messages.length === 0 ? (
        <Panel tone="tint" className="mb-3">
          {/*
            Первым пишет то ассистент, то человек — зависит от задания. Обещать
            сообщение нельзя: по большинству заданий вопросы уже в тексте выше,
            и ожидание выглядит как зависшее приложение
          */}
          <p className="text-[12.5px] leading-[1.5]">
            Вопросы задания — в блоке выше, нажмите на заголовок, чтобы раскрыть. Отвечайте
            своими словами, можно голосом. По части заданий ассистент пишет первым — тогда
            его сообщение появится здесь.
          </p>
        </Panel>
      ) : null}

      <ChatMessages
        messages={messages}
        sending={sending}
        awaitingReply={awaitingReply}
        runPending={runPending}
        onOpenAttachment={(item) => void attachment.open(item)}
      />

      {readyOperations.length > 0 ? (
        <Panel tone="tint" className="mb-3">
          <h3 className="mb-2 text-[13px] font-medium">Материалы от ассистента</h3>
          {readyOperations.map((operation) =>
            (operation.attachments ?? []).map((file) => (
              <AttachmentButton
                key={file.file_id}
                attachment={file}
                onOpen={(item) => void attachment.open(item)}
                className="bd-focus mb-1.5 flex w-full items-center gap-2 rounded-control border border-line/70 bg-surface px-2.5 py-2 text-left text-[12px]"
              />
            ))
          )}
        </Panel>
      ) : null}

      {fault ? <Helper error>{faults[fault].description}</Helper> : error ? <Helper error>{error}</Helper> : null}

      <input
        ref={fileRef}
        type="file"
        className="hidden"
        onChange={(event) => {
          void onPickFile(event.target.files?.[0]);
          // Сброс значения позволяет выбрать тот же файл повторно
          event.target.value = '';
        }}
      />

      <FilePreview
        file={attachment.file}
        onClose={attachment.close}
        onSave={() => void attachment.save()}
      />

      <ActionStack>
        {/*
          Главная кнопка — следующий шаг, а не всегда «Отправить». Сдача
          возможна только после готового итога, и серая кнопка «Отправить»
          при живом диалоге читалась как поломка: подсказка под ней пряталась
          за полем ввода, а нужный шаг жил маленькой кнопкой рядом со скрепкой
        */}
        {step === 'preview' ? (
          <Helper>Когда ответы готовы, соберите итог — его можно посмотреть и отправить на проверку</Helper>
        ) : null}
        {step === 'answer' ? (
          <Helper>Напишите ответ ассистенту — из переписки собирается итог</Helper>
        ) : null}

        {step === 'preview' || step === 'collecting' ? (
          <Button
            variant="primary"
            block
            loading={step === 'collecting'}
            disabled={locked || voice.recording}
            icon={<Send className="h-4 w-4" aria-hidden />}
            onClick={() => void showPreview()}
          >
            {step === 'collecting' ? 'Ассистент собирает итог…' : 'Сформировать итог'}
          </Button>
        ) : (
          <Button
            variant="primary"
            block
            loading={submitting}
            disabled={locked || step !== 'submit'}
            onClick={() => void onSubmit()}
          >
            {submitting ? 'Отправляем…' : 'Отправить на проверку'}
          </Button>
        )}

        <Button
          variant="ghost"
          block
          icon={<MessageSquareOff className="h-4 w-4" aria-hidden />}
          onClick={() => void onCancel()}
        >
          Завершить без отправки
        </Button>
      </ActionStack>
    </Screen>
  );
}
